import React, { useEffect, useMemo, useState } from "react";
import { List, Progress } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { SbTooltip } from "uiCore";
import ChartCard from "../components/ChartCard";
import { hasValue } from "../dashboardData";
import {
  ExpandedContent,
  ExpandedContentHeader,
  AgentDetailList,
  AgentDetailRow,
  ExpandIndicator,
  AgentListItem,
  RoiRow,
  RankAvatar,
  AgentNameCell,
  AgentNameText,
  RoiValueText,
} from "../Dashboard.styles";

const TOP_ACCENT = ["#008080", "#3F88CE", "#8790A4"];

const TOOLTIP_EXPAND_ROW = "Show this agent's description, AI potential, and supported roles/positions.";

// Click-to-expand detail row for one agent — mirrors PAIC V2 Group Agent
// Details' microtask expand-row pattern. `description`, `ai_potential`,
// `roles_supported`, `positions_supported` are optional fields the backend
// may not have deployed yet, so each row only renders when present.
const AgentDetails = ({ agent }) => (
  <ExpandedContent>
    <ExpandedContentHeader>Agent Details</ExpandedContentHeader>
    <AgentDetailList>
      {hasValue(agent.description) && (
        <AgentDetailRow>
          <strong>Description:</strong> {agent.description}
        </AgentDetailRow>
      )}
      {hasValue(agent.ai_potential) && (
        <AgentDetailRow>
          <strong>AI Potential:</strong> {agent.ai_potential}%
        </AgentDetailRow>
      )}
      {hasValue(agent.roles_supported) && (
        <AgentDetailRow>
          <strong>Roles Supported:</strong> {agent.roles_supported}
        </AgentDetailRow>
      )}
      {hasValue(agent.positions_supported) && (
        <AgentDetailRow>
          <strong>Positions Supported:</strong> {agent.positions_supported}
        </AgentDetailRow>
      )}
    </AgentDetailList>
  </ExpandedContent>
);

// AntD `List` + `Avatar` + `Progress` ranking of top AI agents by ROI.
// `agents` is the API's roi-leaderboard response's `agents` array:
// [{ rank, agent_id, agent_name, description, ai_potential, roles_supported,
//    positions_supported, roi, total_value_to_business, estimated_development_cost }]
// — every field beyond rank/agent_name/roi is optional, since the backend can
// ship them independently of the frontend.
const RoiPanel = ({ agents }) => {
  const list = agents || [];
  const maxRoi = useMemo(() => Math.max(...(agents || []).map((a) => a.roi || 0), 1), [agents]);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  // Without agent_id (an optional field), the expand key falls back to rank
  // — so a stale key could otherwise re-attach to a different agent that
  // lands on the same rank after a filter change while this panel stays
  // open. Clearing on every new agents list keeps expand state tied to the
  // list the user actually clicked on.
  useEffect(() => {
    setExpandedKeys(new Set());
  }, [agents]);

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <ChartCard title="Top AI Agents by ROI" subtitle="Highest-performing agents ranked by return on investment">
      <List
        dataSource={list}
        renderItem={(agent, i) => {
          const color = i < 3 ? TOP_ACCENT[i] : "#D8DAE5";
          // agent_name (not rank) is the fallback when agent_id is absent —
          // ROI leaderboards commonly have tied ranks, which would otherwise
          // collide two different agents onto the same expand key.
          const key = agent.agent_id ?? agent.agent_name ?? i;
          const isExpanded = expandedKeys.has(key);
          return (
            <AgentListItem>
              <RoiRow onClick={() => toggleExpand(key)}>
                <RankAvatar size={30} shape="square" $bg={color}>
                  {i + 1}
                </RankAvatar>
                <AgentNameCell>
                  <AgentNameText strong={i < 3}>
                    {agent.agent_name}
                  </AgentNameText>
                  <Progress
                    percent={((agent.roi || 0) / maxRoi) * 100}
                    showInfo={false}
                    strokeColor={color}
                    trailColor="#F0F1F5"
                    strokeLinecap="round"
                    size={["100%", 6]}
                  />
                </AgentNameCell>
                <RoiValueText strong $color={color}>
                  {agent.roi}x ROI
                </RoiValueText>
                <SbTooltip content={TOOLTIP_EXPAND_ROW}>
                  <ExpandIndicator $expanded={isExpanded}>
                    <DownOutlined />
                  </ExpandIndicator>
                </SbTooltip>
              </RoiRow>
              {isExpanded && <AgentDetails agent={agent} />}
            </AgentListItem>
          );
        }}
      />
    </ChartCard>
  );
};

export default RoiPanel;
